import api from "./api";

/**
 * Notification Service
 * Handles FCM token registration and notification management
 */

const notificationService = {
  /**
   * Register FCM token with backend
   */
  registerToken: async (fcmToken, deviceType = "web", deviceId = null) => {
    try {
      const response = await api.post("/api/notifications/register-token", {
        fcm_token: fcmToken,
        device_type: deviceType,
        device_id: deviceId,
      });
      return response.data;
    } catch (error) {
      console.error("Error registering FCM token:", error);
      throw error;
    }
  },

  /**
   * Remove FCM token from backend
   */
  removeToken: async (fcmToken) => {
    try {
      const response = await api.delete("/api/notifications/remove-token", {
        data: { fcm_token: fcmToken },
      });
      return response.data;
    } catch (error) {
      console.error("Error removing FCM token:", error);
      throw error;
    }
  },

  /**
   * Get user's registered FCM tokens
   */
  getTokens: async () => {
    try {
      const response = await api.get("/api/notifications/tokens");
      return response.data;
    } catch (error) {
      console.error("Error getting FCM tokens:", error);
      throw error;
    }
  },

  /**
   * Get notification history
   */
  getHistory: async (limit = 50) => {
    try {
      console.log("📡 [NOTIFICATION SERVICE] Calling API: /api/notifications/history?limit=" + limit);
      const response = await api.get(
        `/api/notifications/history?limit=${limit}`
      );
      console.log("📡 [NOTIFICATION SERVICE] Raw axios response:", response);
      console.log("📡 [NOTIFICATION SERVICE] Response data:", response.data);
      console.log("📡 [NOTIFICATION SERVICE] Response data type:", typeof response.data);
      
      // Axios wraps the response in response.data
      // Backend sends: { success: true, data: { notifications: [...], pagination: {...} } }
      // So response.data is the backend response object
      const backendResponse = response.data;
      
      if (backendResponse && backendResponse.success && backendResponse.data) {
        console.log("📡 [NOTIFICATION SERVICE] Success response with data");
        return backendResponse; // Return the full backend response
      } else {
        console.warn("📡 [NOTIFICATION SERVICE] Unexpected response structure:", backendResponse);
        return backendResponse; // Return as-is and let component handle it
      }
    } catch (error) {
      console.error("❌ [NOTIFICATION SERVICE] Error getting notification history:", error);
      console.error("❌ [NOTIFICATION SERVICE] Error response:", error.response?.data);
      throw error;
    }
  },

  /**
   * Send notification (Admin only)
   */
  sendNotification: async (userId, title, body, data = {}) => {
    try {
      const response = await api.post("/api/notifications/send", {
        user_id: userId,
        title,
        body,
        data,
      });
      return response.data;
    } catch (error) {
      console.error("Error sending notification:", error);
      throw error;
    }
  },

  /**
   * Send notification to user type (Admin only)
   */
  sendNotificationToType: async (userType, title, body, data = {}) => {
    try {
      const response = await api.post("/api/notifications/send-to-type", {
        user_type: userType,
        title,
        body,
        data,
      });
      return response.data;
    } catch (error) {
      console.error("Error sending notification to type:", error);
      throw error;
    }
  },

  /**
   * Mark a single notification as read
   */
  markAsRead: async (notificationId) => {
    try {
      console.log("📡 [NOTIFICATION SERVICE] Marking notification as read:", notificationId);
      const response = await api.put(`/api/notifications/${notificationId}/mark-read`);
      console.log("✅ [NOTIFICATION SERVICE] Mark as read response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ [NOTIFICATION SERVICE] Error marking notification as read:", error);
      console.error("❌ [NOTIFICATION SERVICE] Error response:", error.response?.data);
      console.error("❌ [NOTIFICATION SERVICE] Notification ID:", notificationId);
      throw error;
    }
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async () => {
    try {
      console.log("📡 [NOTIFICATION SERVICE] Marking all notifications as read");
      const response = await api.put("/api/notifications/mark-all-read");
      console.log("✅ [NOTIFICATION SERVICE] Mark all as read response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ [NOTIFICATION SERVICE] Error marking all notifications as read:", error);
      console.error("❌ [NOTIFICATION SERVICE] Error response:", error.response?.data);
      throw error;
    }
  },

  /**
   * Mark multiple notifications as read
   * @param {Array<string>} notificationIds - Array of notification IDs
   */
  markMultipleAsRead: async (notificationIds) => {
    try {
      console.log("📡 [NOTIFICATION SERVICE] Marking multiple notifications as read:", notificationIds);
      const response = await api.put("/api/notifications/mark-multiple-read", {
        notification_ids: notificationIds,
      });
      console.log("✅ [NOTIFICATION SERVICE] Mark multiple as read response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ [NOTIFICATION SERVICE] Error marking multiple notifications as read:", error);
      console.error("❌ [NOTIFICATION SERVICE] Error response:", error.response?.data);
      throw error;
    }
  },
};

export default notificationService;

